import React, {
  useEffect,
  useState,
} from "react";

import {
  FiAlertTriangle,
  FiX,
  FiXCircle,
} from "react-icons/fi";


/* =========================================================
   COMPONENT
========================================================= */

const CancelOrderDialog = ({
  open,

  order,

  loading = false,

  onClose,

  onSubmit,
}) => {
  /* =======================================================
     FORM
  ======================================================= */

  const [
    reason,
    setReason,
  ] = useState("");


  const [
    note,
    setNote,
  ] = useState("");


  /* =======================================================
     ERROR
  ======================================================= */

  const [
    error,
    setError,
  ] = useState("");


  /* =======================================================
     RESET WHEN OPEN
  ======================================================= */

  useEffect(() => {
    if (!open) {
      return;
    }

    setReason("");

    setNote("");

    setError("");

  }, [
    open,
    order?._id,
  ]);


  /* =======================================================
     DO NOT RENDER
  ======================================================= */

  if (!open) {
    return null;
  }


  /* =======================================================
     CLOSE
  ======================================================= */

  const handleClose =
    () => {
      if (loading) {
        return;
      }

      if (
        typeof onClose ===
        "function"
      ) {
        onClose();
      }
    };


  /* =======================================================
     SUBMIT
  ======================================================= */

  const handleSubmit =
    (
      event
    ) => {
      event.preventDefault();


      if (loading) {
        return;
      }


      const cleanReason =
        String(
          reason || ""
        ).trim();


      if (!cleanReason) {
        setError(
          "Cancellation reason is required."
        );

        return;
      }


      if (
        typeof onSubmit ===
        "function"
      ) {
        onSubmit({
          reason:
            cleanReason,

          note:
            String(
              note || ""
            ).trim(),
        });
      }
    };


  /* =======================================================
     ORDER DISPLAY
  ======================================================= */

  const orderNumber =
    order?.orderNumber ||
    "-";


  const customerName =
    order
      ?.customer
      ?.fullName ||
    order
      ?.userId
      ?.name ||
    order
      ?.user
      ?.name ||
    "-";


  const mobile =
    order
      ?.customer
      ?.mobile ||
    order
      ?.userId
      ?.mobile ||
    order
      ?.user
      ?.mobile ||
    "-";


  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div
      className="order-dialog-overlay"
    >
      {/* =================================================
          BACKDROP
      ================================================= */}

      <button
        type="button"
        className="order-dialog-backdrop"
        onClick={
          loading
            ? undefined
            : handleClose
        }
        aria-label="Close cancel order dialog"
      />


      {/* =================================================
          DIALOG
      ================================================= */}

      <div
        className="order-dialog order-cancel-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cancel-order-title"
      >
        {/* ===============================================
            HEADER
        =============================================== */}

        <div
          className="order-dialog-header"
        >
          <div
            className="order-dialog-header-icon cancel"
          >
            <FiXCircle />
          </div>


          <div
            className="order-dialog-header-content"
          >
            <span>
              ORDER CANCELLATION
            </span>

            <h3
              id="cancel-order-title"
            >
              Cancel Order
            </h3>

            <p>
              {orderNumber}
            </p>
          </div>


          <button
            type="button"
            className="order-dialog-close"
            onClick={
              handleClose
            }
            disabled={
              loading
            }
            aria-label="Close"
          >
            <FiX />
          </button>
        </div>


        {/* ===============================================
            ORDER SUMMARY
        =============================================== */}

        <div
          className="order-dialog-order-summary"
        >
          <div>
            <FiXCircle />

            <span>
              Order
            </span>

            <strong>
              {orderNumber}
            </strong>
          </div>


          <div>
            <FiAlertTriangle />

            <span>
              Customer
            </span>

            <strong>
              {customerName}
            </strong>
          </div>
        </div>


        {/* ===============================================
            FORM
        =============================================== */}

        <form
          onSubmit={
            handleSubmit
          }
        >
          <div
            className="order-dialog-body"
          >
            {/* ===========================================
                WARNING
            =========================================== */}

            <div
              className="order-dialog-warning"
            >
              <FiAlertTriangle />

              <div>
                <strong>
                  This action affects inventory
                </strong>

                <span>
                  Cancelling this order will automatically
                  release reserved stock or restore sold
                  stock through the backend inventory
                  transaction.
                </span>
              </div>
            </div>


            {/* ===========================================
                CUSTOMER INFO
            =========================================== */}

            <div
              className="order-cancel-customer-summary"
            >
              <div>
                <span>
                  Customer
                </span>

                <strong>
                  {customerName}
                </strong>
              </div>


              <div>
                <span>
                  Mobile
                </span>

                <strong>
                  {mobile}
                </strong>
              </div>


              <div>
                <span>
                  Current Status
                </span>

                <strong>
                  {String(
                    order?.status ||
                    "-"
                  ).replace(
                    /_/g,
                    " "
                  )}
                </strong>
              </div>


              <div>
                <span>
                  Payment
                </span>

                <strong>
                  {String(
                    order
                      ?.paymentStatus ||
                    "-"
                  ).replace(
                    /_/g,
                    " "
                  )}
                </strong>
              </div>
            </div>


            {/* ===========================================
                CANCELLATION REASON
            =========================================== */}

            <div
              className="order-dialog-field"
            >
              <label>
                Cancellation Reason *
              </label>

              <textarea
                rows={4}
                value={
                  reason
                }
                onChange={(
                  event
                ) => {
                  setReason(
                    event.target
                      .value
                  );

                  setError("");
                }}
                placeholder="Enter the reason for cancelling this order..."
                disabled={
                  loading
                }
                autoFocus
              />

              {error ? (
                <small>
                  {error}
                </small>
              ) : null}
            </div>


            {/* ===========================================
                INTERNAL NOTE
            =========================================== */}

            <div
              className="order-dialog-field"
            >
              <label>
                Internal Note
              </label>

              <textarea
                rows={3}
                value={
                  note
                }
                onChange={(
                  event
                ) =>
                  setNote(
                    event.target
                      .value
                  )
                }
                placeholder="Optional note for admin reference..."
                disabled={
                  loading
                }
              />
            </div>


            {/* ===========================================
                PAYMENT WARNING
            =========================================== */}

            {order
              ?.paymentMethod ===
              "online" &&
            [
              "paid",
              "partially_refunded",
            ].includes(
              order
                ?.paymentStatus
            ) ? (
              <div
                className="order-dialog-refund-warning"
              >
                <FiAlertTriangle />

                <div>
                  <strong>
                    Refund may be required
                  </strong>

                  <span>
                    Cancelling the order restores inventory,
                    but payment refund should be handled
                    separately through the payment flow.
                  </span>
                </div>
              </div>
            ) : null}
          </div>


          {/* ===============================================
              FOOTER
          =============================================== */}

          <div
            className="order-dialog-footer"
          >
            <button
              type="button"
              className="secondary"
              onClick={
                handleClose
              }
              disabled={
                loading
              }
            >
              Keep Order
            </button>


            <button
              type="submit"
              className="danger"
              disabled={
                loading
              }
            >
              {loading ? (
                <>
                  <span
                    className="order-button-spinner"
                  />

                  Cancelling...
                </>
              ) : (
                <>
                  <FiXCircle />

                  Cancel Order
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


export default
  CancelOrderDialog;